import React, { useEffect, useState } from "react";
import api, { money, fmtDate } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { PageHeader, StatusBadge, Spinner, EmptyState } from "@/components/shared";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Package, Plus, Search } from "lucide-react";
import { toast } from "sonner";

const Input = (p) => <input {...p} className="px-3 py-2 rounded-lg border border-slate-300 text-sm outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 w-full" />;
const Sel = ({ children, ...p }) => <select {...p} className="px-3 py-2 rounded-lg border border-slate-300 text-sm bg-white outline-none w-full">{children}</select>;
const Label = ({ children }) => <label className="text-sm font-medium text-slate-700 block mb-1">{children}</label>;
const STATUSES = ["active", "sold", "returned", "expired", "donated", "extended", "archived"];

export default function Items() {
  const { store } = useStore();
  const [rows, setRows] = useState(null);
  const [consignors, setConsignors] = useState([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [open, setOpen] = useState(false);
  const [actionItem, setActionItem] = useState(null);
  const [form, setForm] = useState({ product_name: "", sku: "", consignor_id: "", original_price: "", payout_pct_override: "" });
  const cur = store?.currency || "USD";

  const load = () => { setRows(null); api.get("/items", { params: { search, status } }).then((r) => setRows(r.data)); };
  useEffect(() => { api.get("/consignors").then((r) => setConsignors(r.data.filter((c) => c.status === "active"))); }, [store?.id]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [search, status, store?.id]);

  const save = async () => {
    if (!form.product_name) return toast.error("Product name required");
    try {
      await api.post("/items", {
        ...form, original_price: Number(form.original_price || 0),
        consignor_id: form.consignor_id || null,
        payout_pct_override: form.payout_pct_override ? Number(form.payout_pct_override) : null,
      });
      toast.success("Item added"); setOpen(false);
      setForm({ product_name: "", sku: "", consignor_id: "", original_price: "", payout_pct_override: "" }); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const doAction = async (action) => {
    try { await api.post(`/items/${actionItem.id}/action`, { action }); toast.success(`Item ${action}`); setActionItem(null); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div>
      <PageHeader title="Consignment Items" subtitle="Item-level tracking, prices and expiration">
        <button data-testid="add-item-btn" onClick={() => setOpen(true)} className="flex items-center gap-2 bg-emerald-700 hover:bg-emerald-800 text-white font-semibold px-4 py-2 rounded-lg text-sm">
          <Plus className="w-4 h-4" /> Add Item
        </button>
      </PageHeader>

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input data-testid="item-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name or SKU…"
            className="pl-9 pr-3 py-2 rounded-lg border border-slate-300 text-sm w-full outline-none focus:border-emerald-500" />
        </div>
        <select data-testid="item-status-filter" value={status} onChange={(e) => setStatus(e.target.value)} className="px-3 py-2 rounded-lg border border-slate-300 text-sm bg-white">
          <option value="all">All statuses</option>{STATUSES.map((s) => <option key={s} value={s} className="capitalize">{s}</option>)}
        </select>
      </div>

      {rows === null ? <Spinner /> : rows.length === 0 ? (
        <EmptyState icon={Package} title="No items yet" description="Add consignment items or import them via CSV." />
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                <th className="px-4 py-3">Product</th><th className="px-4 py-3">SKU</th><th className="px-4 py-3">Consignor</th>
                <th className="px-4 py-3 text-right">Price</th><th className="px-4 py-3 text-center">Payout</th>
                <th className="px-4 py-3">Expires</th><th className="px-4 py-3">Status</th><th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((it, i) => (
                <tr key={it.id} data-testid={`item-row-${i}`} className="hover:bg-slate-50">
                  <td className="px-4 py-3 font-medium text-slate-900">{it.product_name}</td>
                  <td className="px-4 py-3 font-mono-fin text-xs text-slate-500">{it.sku || "—"}</td>
                  <td className="px-4 py-3 text-slate-600">{it.consignor_name || <span className="text-rose-600">Unassigned</span>}</td>
                  <td className="px-4 py-3 text-right font-mono-fin">{money(it.current_price, cur)}</td>
                  <td className="px-4 py-3 text-center font-mono-fin">{it.payout_pct}%</td>
                  <td className="px-4 py-3 text-slate-500">{fmtDate(it.expiration_date)}</td>
                  <td className="px-4 py-3"><StatusBadge status={it.status} /></td>
                  <td className="px-4 py-3 text-right">
                    <button data-testid={`item-actions-${i}`} onClick={() => setActionItem(it)} className="text-emerald-700 hover:underline text-xs font-medium">Actions</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add item */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="add-item-dialog">
          <DialogHeader><DialogTitle>Add Consignment Item</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Product name *</Label><Input data-testid="item-name-input" value={form.product_name} onChange={(e) => setForm({ ...form, product_name: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>SKU</Label><Input data-testid="item-sku-input" value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} /></div>
              <div><Label>Original price</Label><Input data-testid="item-price-input" type="number" value={form.original_price} onChange={(e) => setForm({ ...form, original_price: e.target.value })} /></div>
            </div>
            <div><Label>Consignor</Label>
              <Sel data-testid="item-consignor-select" value={form.consignor_id} onChange={(e) => setForm({ ...form, consignor_id: e.target.value })}>
                <option value="">— Select consignor —</option>
                {consignors.map((c) => <option key={c.id} value={c.id}>{c.name} ({c.default_payout_pct}%)</option>)}
              </Sel>
            </div>
            <div><Label>Payout % override (optional)</Label><Input data-testid="item-payout-input" type="number" value={form.payout_pct_override} onChange={(e) => setForm({ ...form, payout_pct_override: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="px-4 py-2 rounded-lg text-slate-600 hover:bg-slate-100 text-sm">Cancel</button>
            <button data-testid="save-item-btn" onClick={save} className="px-4 py-2 rounded-lg bg-emerald-700 hover:bg-emerald-800 text-white font-semibold text-sm">Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Item actions */}
      <Dialog open={!!actionItem} onOpenChange={() => setActionItem(null)}>
        <DialogContent data-testid="item-action-dialog">
          <DialogHeader><DialogTitle>{actionItem?.product_name}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-2">
            {[["extend", "Extend 90 days"], ["return", "Return"], ["donate", "Mark Donated"], ["markdown", "Mark Down 30%"], ["archive", "Archive"]].map(([a, l]) => (
              <button key={a} data-testid={`action-${a}`} onClick={() => doAction(a)} className="px-4 py-2.5 rounded-lg border border-slate-300 hover:border-emerald-500 hover:bg-emerald-50 text-sm font-medium text-slate-700">{l}</button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
